/**
 * catalogueImportColumns: the vocabulary EVERY Lusuku import workbook shares.
 * ───────────────────────────────────────────────────────────────────────────
 * One list of roles, sheet names, section columns, shared product columns,
 * header resolution and self-filling formulas. Each vertical's own columns file
 * imports this, appends the handful of columns only IT has, and exports the
 * result. brandImportColumns.js and healthcareImportColumns.js are both a page
 * long because of it.
 *
 * WHY THIS EXISTS RATHER THAN A SECOND COPY. The brand importer was the only
 * one when it was written, so its sheet vocabulary, its header resolver, its
 * column-letter maths and its To Do formula all lived inside it. Healthcare
 * needed the same nine tenths. A copy would have meant two definitions of
 * "Active", two To Do formulas that would drift the first time one was fixed,
 * and two header resolvers to keep in step with an operator's spelling. The
 * columns a vertical genuinely does not share (brand's tier price list, a
 * pharmacy's batch register) are the ONLY thing its own file holds.
 *
 * The layout follows the China Town workbook, the blueprint every Lusuku
 * product sheet has followed since 26 Sep 2026: a one-line hint above each
 * Title Case header, a Column Guide sheet, a Status column, and columns that
 * fill themselves in.
 *
 * A column's ROLE decides its header colour in the styled blueprint and how the
 * Column Guide describes it:
 *   required  - the row cannot import without it
 *   optional  - imported when filled
 *   auto      - the sheet works it out from other columns; type over it to change
 *   reference - for the team; never imported
 */

import { PRODUCT_PHOTO_RULE_TEXT } from "./productPhotoRules.js";

export const ROLE = Object.freeze({
  REQUIRED: "required",
  OPTIONAL: "optional",
  AUTO: "auto",
  REFERENCE: "reference",
});

/** How the Column Guide words each role. */
export const ROLE_LABEL = Object.freeze({
  required: "Required",
  optional: "Optional",
  auto: "Fills itself in",
  reference: "Not imported",
});

/* Any column header starting with this becomes a key in the product's `specs`
   object. This is what keeps one template usable by every brand. Only a
   vertical whose catalogue actually stores `specs` reads these; healthcare does
   not have the field, so its dialect switches spec columns off rather than
   parsing attributes the server would drop on the floor. */
export const SPEC_PREFIX = "spec:";

export const SECTIONS_SHEET = "1. Sections";
export const PRODUCTS_SHEET = "2. Products";

/* Workbook-level names the formulas lean on. StandInCover holds the file name
   of the vendor's stand-in cover photo, set once on the READ ME sheet. */
export const NAMES = Object.freeze({ STAND_IN_COVER: "StandInCover" });

/** The two Status words. Anything else falls back to yes / no parsing. */
export const STATUS = Object.freeze({ ACTIVE: "Active", DEACTIVATED: "Deactivated" });

/** Image Source value that marks a stand-in cover. */
export const PLACEHOLDER = "Placeholder";

export const col = (key, label, role, hint, extra = {}) => ({
  key,
  label,
  role,
  hint,
  aliases: [],
  ...extra,
});

/* Sections are the same idea on every vertical: a named group a shopper
   browses, with counts that fill themselves in. Nothing here is brand-specific
   or pharmacy-specific, so there is one list. */
export const SECTION_COLUMNS = [
  col("section_order", "Order", ROLE.OPTIONAL, "1, 2, 3 ... the order sections are listed in."),
  col("section_title", "Section", ROLE.REQUIRED, "The group shoppers browse, e.g. Kitchen & Dining."),
  col("section_description", "Description", ROLE.OPTIONAL, "One line about what belongs here."),
  col("product_count", "Products", ROLE.AUTO, "Products on the Products sheet in this section."),
  col("priced_count", "Priced", ROLE.AUTO, "How many of them have a price."),
  col("active_count", "Active", ROLE.AUTO, "How many of them have Status Active."),
  col("notes", "Notes", ROLE.REFERENCE, "For your team. Not imported."),
];

// ── Shared product column groups ───────────────────────────────────────────
/* Grouped rather than listed flat so each vertical can slot its own columns in
   at the right point and still read left to right the way the blueprint does:
   who it is, what it costs, whether it can be bought, what it looks like, then
   the commercial fine print. */

/** Who the product is. */
export const IDENTITY_COLUMNS = [
  col("row_id", "Row ID", ROLE.REFERENCE, "Your own reference. Not imported."),
  col("section_title", "Section", ROLE.REQUIRED, "Pick one from the Sections sheet."),
  col("name", "Name", ROLE.REQUIRED, "As a shopper should see it. Names may repeat; the codes tell them apart."),
  col("item_code", "Item Code", ROLE.REFERENCE, "The vendor's own code for the product. Not imported yet.", {
    detail: "How the vendor finds the product in their own stock. Kept in the sheet for the team; Lusuku does not store it yet.",
  }),
  col("sku_code", "Barcode", ROLE.OPTIONAL, "Barcode or SKU printed on the product.", {
    aliases: ["sku", "barcode_sku", "barcode_or_sku"],
    detail: "Also how a re-upload knows a product is already on Lusuku, so keep it unchanged once imported.",
  }),
  col("enabled", "Status", ROLE.OPTIONAL, "Active or Deactivated. Filter this column to see either.", {
    aliases: ["online_status"],
    detail: "What the import sets. With 'Import everything deactivated' on, every product lands Deactivated whatever this says. It does not follow later changes made on Lusuku.",
  }),
  col("to_do", "To Do", ROLE.AUTO, "What the product still needs before it can go live.", {
    detail: "Shows Ready, or the missing items: Section, Name, Price, Photo. Updates as you type.",
  }),
  col("description", "Description", ROLE.OPTIONAL, "Shown on the product page."),
];

/** What it costs. */
export const PRICING_COLUMNS = [
  col("price_ugx", "Price (UGX)", ROLE.REQUIRED, "Gross price the customer pays.", {
    detail: "Needed before a product goes live, unless Price on Request is Yes. Lusuku works out commission from it; never enter the net price.",
  }),
  col("price_on_request", "Price on Request", ROLE.OPTIONAL, "Yes for a product you sell by quote.", {
    detail: "Leave blank for an ordinary product. Yes hides the price and shoppers ask you for one instead, so only use it for goods you really do quote. A product you simply have not priced yet is not this: leave it blank, fill the price in later, and To Do will keep reminding you.",
  }),
];

/** Whether it can be bought. */
export const STOCK_COLUMNS = [
  col("availability", "Availability", ROLE.OPTIONAL, "In Stock or Out of Stock.", {
    detail: "Out of Stock rows are skipped by the import, not created with zero stock.",
  }),
  col("count_in_stock", "Count in Stock", ROLE.OPTIONAL, "Units available. Blank means 1."),
];

/** What it looks like. */
export const MEDIA_COLUMNS = [
  col("image_filename", "Image File", ROLE.REQUIRED, `Cover photo file name, e.g. tv-32.jpg. ${PRODUCT_PHOTO_RULE_TEXT}.`, {
    detail: "Upload the photo files with the workbook; names are matched ignoring capitalisation. Not needed when Image URL is filled. The READ ME sheet has the full photo rules.",
  }),
  col("cloudinary_url", "Image URL", ROLE.OPTIONAL, "A Lusuku CDN link, if the photo is already uploaded.", {
    detail: "A Drive or shop page link is not an image and is refused.",
  }),
  col("additional_image_filenames", "Extra Images", ROLE.OPTIONAL, "Up to 5 more photo files, comma separated.", {
    detail: `Same rules as the cover: ${PRODUCT_PHOTO_RULE_TEXT}.`,
  }),
  col("image_source", "Image Source", ROLE.AUTO, "Placeholder while the cover is a stand-in.", {
    detail: "Fills itself in from Image File and the stand-in cover named on the READ ME. Products marked Placeholder show 'Needs photo' on Lusuku.",
  }),
];

/** How a shopper finds it, and the trade terms every catalogue carries. */
export const MERCHANDISING_COLUMNS = [
  col("tags", "Tags", ROLE.OPTIONAL, "Search words, comma separated."),
  col("platform_categories", "Platform Categories", ROLE.OPTIONAL, "Lusuku category names, comma separated."),
  col("unit", "Unit", ROLE.OPTIONAL, "Unit for Measurements, e.g. Litres."),
  col("measurements", "Measurements", ROLE.OPTIONAL, "The number that goes with the unit. Blank means 1."),
  col("min_order_qty", "Min Order Qty", ROLE.OPTIONAL, "Smallest quantity a buyer may order. Blank means 1."),
];

/** On every catalogue that stores `requiredPaymentMethod`. */
export const PAYMENT_COLUMN = col(
  "required_payment_method",
  "Payment Method",
  ROLE.OPTIONAL,
  "Blank means any. Or ANY_ONLINE, MOMO, AIRTEL, VISA, ZAMTEL."
);

/** Always last, on every sheet. */
export const NOTES_COLUMN = col("notes", "Notes", ROLE.REFERENCE, "For your team. Not imported.");

// ── Headers ────────────────────────────────────────────────────────────────

/**
 * "Price (UGX)" → "price_ugx", "Row ID" → "row_id", "price_ugx" → itself.
 * A spec header keeps its prefix: "Spec: Model" → "spec:model".
 */
export const headerKey = (raw) => {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s) return "";
  if (s.startsWith(SPEC_PREFIX)) return `${SPEC_PREFIX}${s.slice(SPEC_PREFIX.length).trim()}`;
  return s.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
};

/**
 * The header resolver for ONE vertical's column set.
 *
 * Per vertical rather than global, because the same key can carry a different
 * label: a vertical is free to word its own Status hint or rename a column it
 * owns, and a resolver shared across all of them would hand back whichever
 * label loaded last. The alias maps are built once, on first use, and cached.
 *
 * @param {object} columnsByKind { sections: Column[], products: Column[] }
 */
export const createHeaderResolver = (columnsByKind) => {
  const cache = {};
  const aliasMap = (kind) => {
    if (!cache[kind]) {
      const m = new Map();
      for (const c of columnsByKind[kind] || []) {
        m.set(c.key, c.key);
        m.set(headerKey(c.label), c.key);
        for (const a of c.aliases || []) m.set(headerKey(a), c.key);
      }
      cache[kind] = m;
    }
    return cache[kind];
  };

  /** The field key a header means on a sheet of this kind. Unknown headers pass through. */
  const canonicalHeader = (kind, raw) => {
    const k = headerKey(raw);
    if (!k || k.startsWith(SPEC_PREFIX)) return k;
    return aliasMap(kind).get(k) || k;
  };

  /** The Title Case label for a key, for messages an operator reads. */
  const labelOf = (kind, key) =>
    (columnsByKind[kind] || []).find((c) => c.key === key)?.label || key;

  return { canonicalHeader, labelOf, COLUMNS_BY_KIND: Object.freeze({ ...columnsByKind }) };
};

/** The header a spec field gets in a template. */
export const specHeader = (fieldKey) => `Spec: ${fieldKey}`;

// ── Formulas ───────────────────────────────────────────────────────────────

/** 0 → A, 25 → Z, 26 → AA. */
export const columnLetter = (index) => {
  let n = index + 1;
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

/** { key: letter } for a column list. */
export const columnLetters = (columns) =>
  Object.fromEntries(columns.map((c, i) => [c.key, columnLetter(i)]));

/* A row counts as empty when it has neither a Section nor a Name, so the
   formulas copied down past the last product stay blank and the importer skips
   those rows. */
const blankRow = (L, r) => `AND(${L.section_title}${r}="",${L.name}${r}="")`;

/**
 * Formulas for the product columns whose role is AUTO, as functions of the
 * column letters (L) and a row number (r). Excel and Google Sheets functions
 * only, so the file stays an ordinary .xlsx.
 *
 * Written against the SHARED column keys alone (section_title, name, price_ugx,
 * price_on_request, image_filename, cloudinary_url, image_source), so a
 * vertical that adds columns of its own inherits both formulas unchanged and
 * the letters resolve from its own list.
 */
export const PRODUCT_FORMULAS = {
  /* `price_on_request` IS NO LONGER A FORMULA, and removing it is the point.
     It used to be `IF(price>0,"No","Yes")`, so every row nobody had costed yet
     arrived declaring itself sold by quote. That erases the one distinction the
     activation gate depends on. models/shared/tradePricingFields.js puts it
     plainly: an import lands at price 0 because nobody has typed the price yet,
     and listing that is a USh 0 product with an Add to Cart button, whereas
     "price on request" is a deliberate commercial posture. QC's refusal to
     activate an unpriced product (`catalogueListingProblem`) returns null the
     moment `priceOnRequest` is true, so the auto-Yes was switching off the very
     check that was meant to catch the unpriced row, and the product went live
     saying "Price on request" when the truth was that nobody had priced it.

     The sheet contradicted itself about this too: the same row showed
     "Price on Request: Yes" and "To Do: Price", because `to_do` below has
     always counted `price<=0` as missing. To Do was right. The column is now an
     ordinary optional one the vendor types Yes into on purpose, and a blank
     reads as No through `parseBool`, which is what it already did for every
     value it did not recognise. */
  image_source: (L, r) =>
    `IF(${blankRow(L, r)},"",IF(AND(${L.cloudinary_url}${r}="",OR(${L.image_filename}${r}="",${L.image_filename}${r}=${NAMES.STAND_IN_COVER})),"${PLACEHOLDER}",""))`,
  to_do: (L, r) => {
    const missing =
      `IF(${L.section_title}${r}="","Section ","")` +
      `&IF(${L.name}${r}="","Name ","")` +
      `&IF(AND(N(${L.price_ugx}${r})<=0,${L.price_on_request}${r}<>"Yes"),"Price ","")` +
      `&IF(${L.image_source}${r}="${PLACEHOLDER}","Photo ","")`;
    return `IF(${blankRow(L, r)},"",IF(${missing}="","Ready",SUBSTITUTE(TRIM(${missing})," ",", ")))`;
  },
};

/**
 * Formulas for the section columns whose role is AUTO. `P(key)` returns the
 * absolute range of a product column, e.g. '2. Products'!$B$3:$B$1000.
 */
export const SECTION_FORMULAS = {
  product_count: (S, r, P) =>
    `IF(${S.section_title}${r}="","",COUNTIF(${P("section_title")},${S.section_title}${r}))`,
  /* "Priced" counts a row as settled when it has a price OR is deliberately
     sold by quote, because both are ready to go live. Counting only `price>0`
     made a section of quote-only goods look permanently unfinished. */
  priced_count: (S, r, P) =>
    `IF(${S.section_title}${r}="","",COUNTIFS(${P("section_title")},${S.section_title}${r},${P("price_ugx")},">0")` +
    `+COUNTIFS(${P("section_title")},${S.section_title}${r},${P("price_on_request")},"Yes"))`,
  active_count: (S, r, P) =>
    `IF(${S.section_title}${r}="","",COUNTIFS(${P("section_title")},${S.section_title}${r},${P("enabled")},"${STATUS.ACTIVE}"))`,
};

/**
 * Builds the `productRange` helper for one vertical's product column list:
 * productRange(first, last)(key) is that column over rows first..last.
 */
export const makeProductRange = (productColumns) => (first, last) => {
  const L = columnLetters(productColumns);
  return (key) => `'${PRODUCTS_SHEET}'!$${L[key]}$${first}:$${L[key]}$${last}`;
};
